import { useState, useEffect, useCallback } from 'react';

interface UserLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

interface UseUserLocationResult {
  location: UserLocation | null;
  error: string | null;
  loading: boolean;
  permissionStatus: PermissionState | 'unsupported' | null;
  requestPermission: () => Promise<void>;
  timezone: string;
}

const LOCATION_STORAGE_KEY = 'ht-user-location';
const PERMISSION_STORAGE_KEY = 'ht-location-permission';

/**
 * Hook for managing user geolocation
 * - Requests permission on demand
 * - Caches location in sessionStorage
 * - Provides timezone info for date calculations
 */
export function useUserLocation(): UseUserLocationResult {
  const [location, setLocation] = useState<UserLocation | null>(() => {
    // Try to restore from session storage
    const cached = sessionStorage.getItem(LOCATION_STORAGE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<PermissionState | 'unsupported' | null>(null);

  // Get the user's timezone (available without location permission)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Check permission status on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setPermissionStatus('unsupported');
      return;
    }

    // Check if permission API is available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);

        // Listen for permission changes
        result.addEventListener('change', () => {
          setPermissionStatus(result.state);
        });

        // If already granted, get location automatically
        if (result.state === 'granted' && !location) {
          getCurrentPosition();
        }
      }).catch(() => {
        // Permission API not supported for geolocation in this browser
        // Check localStorage for previous permission
        const wasGranted = localStorage.getItem(PERMISSION_STORAGE_KEY);
        if (wasGranted === 'granted' && !location) {
          getCurrentPosition();
        }
      });
    }
  }, []);

  const getCurrentPosition = useCallback(() => {
    console.log('[useUserLocation] getCurrentPosition called');
    if (!navigator.geolocation) {
      console.log('[useUserLocation] Geolocation not supported');
      setError('Geolocation is not supported by your browser');
      return;
    }

    console.log('[useUserLocation] Requesting geolocation...');
    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('[useUserLocation] Got position:', position.coords.latitude, position.coords.longitude);
        const newLocation: UserLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        };

        setLocation(newLocation);
        setError(null);
        setLoading(false);
        setPermissionStatus('granted');

        // Cache in session storage
        sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
        localStorage.setItem(PERMISSION_STORAGE_KEY, 'granted');
      },
      (err) => {
        console.log('[useUserLocation] Error:', err.code, err.message);
        setLoading(false);

        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Location permission denied');
            setPermissionStatus('denied');
            localStorage.setItem(PERMISSION_STORAGE_KEY, 'denied');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Location information unavailable');
            break;
          case err.TIMEOUT:
            setError('Location request timed out');
            break;
          default:
            setError('An unknown error occurred');
        }
      },
      {
        enableHighAccuracy: false, // Don't need high accuracy for map pin
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  }, []);

  const requestPermission = useCallback(async () => {
    console.log('[useUserLocation] requestPermission called');
    getCurrentPosition();
  }, [getCurrentPosition]);

  return {
    location,
    error,
    loading,
    permissionStatus,
    requestPermission,
    timezone,
  };
}
